import { useVersionedContext } from '@backstage/version-bridge';
import type { Entity } from '@backstage/catalog-model';

/**
 * L'entité courante, ou `undefined` quand il n'y en a pas.
 *
 * `useAsyncEntity` de `@backstage/plugin-catalog-react` **lève** une exception
 * hors d'une page d'entité — « Entity context is not available ». C'est le bon
 * comportement pour un composant qui n'a de sens que sur une entité, mais il
 * interdit de monter un composant *global* qui s'adapte à l'entité quand il y
 * en a une.
 *
 * C'est exactement le cas du sélecteur d'environnement : il appartient à la
 * coquille de l'application, et se projette dans l'en-tête de la page courante,
 * qu'elle porte une entité ou non.
 *
 * On lit donc le même contexte versionné que Backstage, sans son garde-fou.
 * Si la forme du contexte change en amont, ce hook renverra `undefined` au lieu
 * de planter — la dégradation va dans le bon sens.
 */
export function useOptionalEntity(): Entity | undefined {
  const holder = useVersionedContext<{ 1: { entity?: Entity } }>(
    'entity-context',
  );
  return holder?.atVersion(1)?.entity;
}
